/**
 * DataTable Component
 * A reusable table component that displays data based on provided column definitions and row data.
 * Supports loading states, empty states, and optional row click handling.
 *
 * @param {Array} columns - Column definitions array. Each entry:
 *   - key {string}:        Unique column identifier, used to access row[key] by default.
 *   - header {string}:     Display text for the column header (<th>).
 *   - getValue {Function}: (optional) Custom accessor — receives the row, returns the cell value.
 *   - render {Function}:   (optional) Custom renderer — receives (value, row), returns a React node.
 *
 * @param {Array}    rows        - Data objects to render as table rows.
 * @param {Function} rowKey      - Derives a unique React key per row. Defaults to (row) => row.id.
 * @param {boolean}  isLoading   - When true, shows a skeleton/placeholder instead of the table.
 * @param {string}   emptyText   - Message shown when `rows` is empty. Defaults to "No data".
 * @param {Function} onRowClick  - (optional) Called with the row object on click; adds hover styling.
 */

import { isValidElement, useEffect, useRef } from "react";
import { Spinner, TablePlaceHolder } from "../LoadingAnimations/LoadingAnimations";

const DataTable = ({
  columns,
  rows,
  rowKey = (row) => row.id,
  isLoading = false,
  emptyText = "No data",
  onRowClick = null,

  // Optional infinite-scroll props
  enableScrollLoad = false,
  hasMore = false,
  isLoadingMore = false,
  onLoadMore = null,
}) => {
  const loadMoreRef = useRef(null);

  useEffect(() => {
    if (!enableScrollLoad) return;
    if (!hasMore) return;
    if (!onLoadMore) return;

    const target = loadMoreRef.current;
    if (!target) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const firstEntry = entries[0];

        if (firstEntry.isIntersecting && hasMore && !isLoadingMore) {
          onLoadMore();
        }
      },
      {
        root: null, // viewport
        rootMargin: "0px",
        threshold: 1.0, // trigger when the target is fully visible
      }
    );
    observer.observe(target);

    return () => {
      observer.disconnect();
    };
  }, [enableScrollLoad, hasMore, isLoadingMore, onLoadMore, rows.length]);

  // Show skeleton placeholder while data is being fetched
  if (isLoading) return <TablePlaceHolder />;

  return (
    <div className="table-cont">
      <table id="forwardTable" className="yard-table">
        {/* -------- Column Headers -------- */}
        <thead>
          <tr className="table-header">
            {columns.map((col) => (
              <th key={col.key} scope="col">
                {col.header}
              </th>
            ))}
          </tr>
        </thead>

        {/* -------- Data Rows -------- */}
        <tbody>
          {/* Show empty-state message when there are no rows to display */}
          {!rows?.length && (
            <tr className="data-rows">
              <td colSpan={columns.length} className="gray">
                {emptyText}
              </td>
            </tr>
          )}

          {rows.map((row) => {
            const key = rowKey(row);

            return (
              <tr
                key={key}
                onClick={onRowClick ? () => onRowClick(row) : null}
                // Apply hover style only when rows are clickable
                className={`data-rows ${onRowClick ? "row-hover" : ""}`}
              >
                {columns.map((col) => {
                  // Use custom getValue if provided; otherwise fall back to row[col.key]
                  const value = col.getValue ? col.getValue(row) : row[col.key];

                  return (
                    <td className="gray" key={`${key}-${col.key}`}>
                      {/* Use custom render if provided; otherwise stringify the value */}
                      {col.render
                        ? col.render(value, row)
                        : isValidElement(value)
                        ? value
                        : String(value ?? "N/A")}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>

      {enableScrollLoad && (
        <div ref={loadMoreRef} style={{height: "20px"}} aria-hidden="true"/>
      )}

      {enableScrollLoad && isLoadingMore && (
        <div className="gray" style={{ padding: "12px", textAlign: "center"}}>
          <Spinner/>
        </div>
      )}
    </div>
  );
};

export default DataTable;
